import {retryWithBackoff} from "../../rxjs/retryWithBackoff";
import {Expiration} from "../../interfaces/Expiration";
import {from, Observable, of, shareReplay, switchMap, tap} from "rxjs";
import {catchError, concatMap} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {Log, LogTag} from "../Log";
import {ILogInfo} from "../../interfaces/ILogInfo";
import {CachingService} from "./caching.service";


export class CacheItem {
    cachingService: CachingService;
    httpClient: HttpClient;
    url: string;
    folder: string;
    proxyUrl: string;
    put: Observable<any>;

    constructor(cachingService: CachingService, httpClient: HttpClient, url: string, folder: string, proxyUrl: string) {
        this.cachingService = cachingService;
        this.httpClient = httpClient;
        this.url = url;
        this.proxyUrl = proxyUrl;
        this.folder = folder;
    }


    request(expiration: number): Observable<any> {
        this.cachingService.updateLastUsed(this.folder, this.url);
        return from(caches.match(this.url)).pipe(
            switchMap(cachedResponse => {
                if (cachedResponse) {
                    const created = this.cachingService.foldersMap[this.folder][this.url].created;
                    if (this.isExpired(created, expiration)) {
                        return this.putInCached(this.url, this.folder, cachedResponse);
                    } else {
                        return new Observable<any>( sub => {
                            this.getBody(cachedResponse).then(body => {
                                const data = this.getData(body);
                                sub.next(data);
                                sub.complete();
                            });
                        })
                    }
                } else {
                    return this.putInCached(this.url, this.folder);
                }
            })
        )
    }

    protected putInCached(url: string, folder: string, cachedResponse = null): Observable<any> {
        const logInfo = Log.getLogInfo("CacheItem", "putInCached", [url, folder]);
        Log.d(logInfo, LogTag.CACHE, 'cachedResponse=' + cachedResponse);
        if (this.put == null) {
            if (cachedResponse == null) {
                this.put = this.fetchCache(url, folder).pipe(
                    retryWithBackoff(),
                    shareReplay(),
                    tap({
                        complete: () => {
                            this.put = null;
                        }
                    })
                )
            } else {
                this.put = this.fetchCache(url, folder).pipe(
                    shareReplay(),
                    tap({
                        complete: () => {
                            this.put = null;
                        }
                    }),
                    catchError(err => {

                        return new Observable<any>( sub => {
                            this.getBody(cachedResponse).then(body => {
                                const data = this.getData(body);
                                sub.next(data);
                                sub.complete();
                            });
                        })

                    })
                )
            }
        }
        return this.put;
    }

    protected isExpired(created: number, expiration: number): boolean {
        let expired;
        if (expiration === Expiration.Now) {
            expired = true;
        } else if (expiration === Expiration.Never) {
            expired = false;
        } else {
            const now = new Date().getTime();
            expired = (now - created) > expiration;
        }

        return expired;
    }

    protected fetchCache(url: string, folder: string): Observable<any> {
        const logInfo = Log.getLogInfo("CacheItem", "fetchCache", [url, folder]);
        return new Observable<any>(sub => {
            let fetchUrl;
            let fetchInit;
            if (this.proxyUrl != null) {
                fetchUrl = this.proxyUrl
                fetchInit = {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({url: url})
                }
            } else {
                fetchUrl = url + (url.indexOf('?') != -1 ? '&' : '?') + 'reload=force';
            }
            fetch(fetchUrl, fetchInit).then(response => {
                if (response.status == 200) {
                    caches.open(folder).then(cache => {
                        const copy = response.clone();
                        const headers = new Headers(copy.headers);
                        this.cachingService.updateLastUsed(folder, url);

                        return this.getBody(copy).then(body => {
                            const data = this.getData(body);
                            return cache.put(url, new Response(body, {
                                status: copy.status,
                                statusText: copy.statusText,
                                headers: headers
                            })).then(a => {
                                Log.d(logInfo, LogTag.CACHE, 'put in cache success');
                                sub.next(data);
                                sub.complete();
                            });
                        });
                    })
                } else {
                    Log.w(logInfo, LogTag.CACHE, 'status=' + response.status);
                    sub.error({code: response.status, url: fetchUrl});
                }
            }).catch(error => {
                const msg = (navigator.onLine) ? "cors" : "offline"
                Log.w(logInfo, LogTag.CACHE, 'fetch fail cause: ' + msg);
                sub.error({code: msg, url: fetchUrl, message: "fetch fail"});
            })

        })
    }



    getBody(response: Response): Promise<any> {
        return null;
    }

    getData(body): any {
        return null;
    }
}
